import { MainApi } from 'api/MainApi'
import { type GausiumExecutableTaskRecent, RobotApi } from 'api/RobotApi'
import Loader from 'components/Loader/Loader'
import ScenarioTable from 'components/ScenarioTable/ScenarioTable'
import { IScenarioInfo } from 'components/SharedScenarioItem/SharedScenarioItem'
import type { DialogTarget } from 'pages/TaskScheduler/TaskSettingsModal/DialogContext'
import TaskSettingsModal from 'pages/TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PROGRESS_TYPES } from 'utils/constants'
import { enrichGausiumExecTasksWithMapPreviews } from 'utils/gausiumScenarioFromExecTask'
import { VENDOR } from 'utils/vendorDisplayName'

import style from '../FacilityView.module.css'

const PAGE_SIZE = 20

export type GausiumScenariosTabContentProps = {
  gausiumRobotIds: number[]
  /** When set, load tasks for all facility GS robots in one request. */
  facilityId?: number
  /** Facility: controlled page + refresh. Robot: omit for internal state. */
  scenariosPage?: number
  scenariosRefreshKey?: number
  onScenarioListRefreshNeeded?: () => void
  /** Full list for parent map/preview lookup. */
  onSyncScenarioState?: (scenarios: IScenarioInfo[], totalCount: number) => void
  /** Facility: map + table highlight. */
  highlightedScenarioId?: number
  /**
   * Facility: row click (highlight + mission ids for map).
   * Robot: omit — internal re-click toggle like Facility `handleScenarioSelect`.
   */
  onScenarioClick?: (id: number, missionIds: Array<number | string>) => void
  /** Autonomics launch is handled by parent; GS opens TaskSettingsModal here. */
  onLaunchAutonomicsScenario?: (scenario: IScenarioInfo) => void
  className?: string
  scenariosTableClassName?: string
  hideRobotColumn?: boolean
  hideIdColumn?: boolean
}

async function loadExecutableTasksForScope(
  robotIds: number[],
  facilityId?: number
): Promise<GausiumExecutableTaskRecent[]> {
  if (facilityId != null && robotIds.length > 1) {
    const rows = await MainApi.fetchFacilityGausiumExecutableTasks(facilityId)
    return rows as GausiumExecutableTaskRecent[]
  }
  if (robotIds.length === 0) return []
  const lists = await Promise.all(robotIds.map(id => RobotApi.fetchGausiumExecutableTasksRecent(id)))
  return lists.flat()
}

/**
 * Gausium executable tasks for the Scenarios tab (Facility GS-only, or single GS robot page).
 */
export default function GausiumScenariosTabContent({
  gausiumRobotIds,
  facilityId,
  scenariosPage: controlledPage,
  scenariosRefreshKey: controlledRefreshKey,
  onScenarioListRefreshNeeded,
  onSyncScenarioState,
  highlightedScenarioId: parentHighlightedId,
  onScenarioClick,
  onLaunchAutonomicsScenario,
  className,
  scenariosTableClassName,
  hideRobotColumn = false,
  hideIdColumn = false,
}: GausiumScenariosTabContentProps) {
  const [internalPage, setInternalPage] = useState(1)
  const [internalRefreshKey, setInternalRefreshKey] = useState(0)
  const [localHighlight, setLocalHighlight] = useState<number | undefined>(undefined)

  const scenariosPage = controlledPage ?? internalPage
  const scenariosRefreshKey = controlledRefreshKey ?? internalRefreshKey

  const syncRef = useRef(onSyncScenarioState)
  syncRef.current = onSyncScenarioState

  const [allScenarios, setAllScenarios] = useState<IScenarioInfo[]>([])
  const [scenariosProgress, setScenariosProgress] = useState(PROGRESS_TYPES.IDLE)

  const idsKey = useMemo(
    () =>
      gausiumRobotIds
        .slice()
        .sort((a, b) => a - b)
        .join(','),
    [gausiumRobotIds]
  )

  const pagedScenarios = useMemo(() => {
    const start = (scenariosPage - 1) * PAGE_SIZE
    return allScenarios.slice(start, start + PAGE_SIZE)
  }, [allScenarios, scenariosPage])

  useEffect(() => {
    if (gausiumRobotIds.length === 0) {
      setAllScenarios([])
      setScenariosProgress(PROGRESS_TYPES.SUCCESS)
      syncRef.current?.([], 0)
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        setScenariosProgress(PROGRESS_TYPES.WORK)
        const raw = await loadExecutableTasksForScope(gausiumRobotIds, facilityId)
        if (cancelled) return
        const enriched = await enrichGausiumExecTasksWithMapPreviews(raw)
        if (cancelled) return
        setAllScenarios(enriched)
        setScenariosProgress(PROGRESS_TYPES.SUCCESS)
        syncRef.current?.(enriched, enriched.length)
      } catch {
        if (!cancelled) setScenariosProgress(PROGRESS_TYPES.ERROR)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [idsKey, scenariosRefreshKey, gausiumRobotIds, facilityId])

  const handleScenarioSelectLocal = useCallback(
    (id: number) => {
      if (localHighlight === id) {
        setLocalHighlight(undefined)
        setTimeout(() => setLocalHighlight(id), 0)
        return
      }
      setLocalHighlight(id)
    },
    [localHighlight]
  )

  const tableHighlight = onScenarioClick ? parentHighlightedId : localHighlight

  const handleRowClick = (id: number, missionIds: Array<number | string>) => {
    if (onScenarioClick) {
      onScenarioClick(id, missionIds)
      return
    }
    handleScenarioSelectLocal(id)
  }

  const refreshList = () => {
    if (onScenarioListRefreshNeeded) {
      onScenarioListRefreshNeeded()
    } else {
      setInternalPage(1)
      setInternalRefreshKey(k => k + 1)
    }
  }

  const [gausiumLaunchTask, setGausiumLaunchTask] = useState<GausiumExecutableTaskRecent | null>(null)

  return (
    <>
      <div className={className ?? style.missionsTab}>
        {scenariosProgress === PROGRESS_TYPES.WORK && <Loader />}
        {scenariosProgress === PROGRESS_TYPES.ERROR && (
          <div className={style.emptyWrapper}>
            <div className={style.emptyTitle}>Error loading scenarios</div>
          </div>
        )}
        {scenariosProgress === PROGRESS_TYPES.SUCCESS && (
          <ScenarioTable
            className={scenariosTableClassName ?? style.scenariosTableRoot}
            scenarios={pagedScenarios}
            highlightedScenarioId={tableHighlight}
            onScenarioClick={handleRowClick}
            onLaunchScenario={scenario => {
              if (scenario.is_gausium_executable_task && scenario.gausium_exec_task) {
                setGausiumLaunchTask(scenario.gausium_exec_task)
                return
              }
              onLaunchAutonomicsScenario?.(scenario)
            }}
            onScenariosUpdated={refreshList}
            hideIdColumn={hideIdColumn}
            hideRobotColumn={hideRobotColumn}
            hideDelete={true}
            robotColumnTitle={hideRobotColumn ? 'Robot type' : 'Robot'}
          />
        )}
      </div>
      {(() => {
        const target: DialogTarget = { kind: 'new' }
        const preselectedData = gausiumLaunchTask
          ? { gausiumExecutableTask: gausiumLaunchTask, robotId: gausiumLaunchTask.robot_id }
          : undefined
        return (
          <TaskSettingsModal
            isOpen={gausiumLaunchTask != null}
            taskName=""
            onClose={() => setGausiumLaunchTask(null)}
            onSubmit={() => {
              setGausiumLaunchTask(null)
              refreshList()
            }}
            target={target}
            preselectedData={preselectedData}
            pickerScope={{ vendor: VENDOR.GAUSIUM }}
            dateToEdit={null}
          />
        )
      })()}
    </>
  )
}
